import { RouteLocationNormalizedLoaded } from 'vue-router';
import { User } from '@/store/modules/auth';

const BYPASS_ROLES = ['gerant', 'superadmin', 'super_admin'];
const SUPERADMIN_ROLES = ['superadmin', 'super_admin'];
const SUPPORT_ROLES = ['support'];
const DEMO_ROLES = ['demo'];
const DEMO_BLOCKED_PERMISSIONS = ['users.delete'];

export const ROUTE_PERMISSIONS: Record<string, string> = {
  Dashboard: 'dashboard.view',
  ProductDatabase: 'products.view',
  ProductPromotions: 'promotions.view',
  Products: 'products.view',
  ProductShow: 'products.view',
  StockMovements: 'stock_movements.view',
  ElectronicWallets: 'wallet.view',
  StockTransfers: 'stock_transfers.view',
  StockTransferCreate: 'stock_transfers.create',
  StockTransferEdit: 'stock_transfers.update',
  StockTransferShow: 'stock_transfers.view',
  Inventories: 'inventories.view',
  InventoryShow: 'inventories.view',
  InventoryCount: 'inventories.count',
  CashierInventories: 'inventories.count',
  CashierInventoryCount: 'inventories.count',
  Purchases: 'purchases.view',
  PurchaseShow: 'purchases.view',
  Expenses: 'expenses.view',
  ExpenseShow: 'expenses.view',
  ExpenseEdit: 'expenses.update',
  ExpenseTypes: 'expenses.manage_types',
  ExpensesReport: 'reports.view',
  SupplierReturns: 'supplier_returns.view',
  SupplierReturnCreate: 'supplier_returns.create',
  SupplierReturnEdit: 'supplier_returns.update',
  SupplierReturnShow: 'supplier_returns.view',
  Suppliers: 'suppliers.view',
  SupplierShow: 'suppliers.view',
  Clients: 'clients.view',
  ClientShow: 'clients.view',
  Sales: 'sales.view',
  SalesQuotes: 'sales.view',
  SalesDaySummary: 'cash_counts.view',
  SaleShow: 'sales.view',
  CashRegisters: 'cash_registers.view',
  CashRegisterShow: 'cash_registers.view',
  CashCounts: 'cash_counts.view',
  CashCountShow: 'cash_counts.view',
  CashierSales: 'sales.create',
  CashierEndOfDay: 'cash_counts.create',
  DeliveriesView: 'deliveries.view',
  DeliveryShow: 'deliveries.view',
  Rays: 'products.view',
  RayShow: 'products.view',
  RayProductAssignment: 'products.view',
  Categories: 'products.view',
  CategoryShow: 'products.view',
  CategoryProductAssignment: 'products.view',
  SalesReport: 'reports.view',
  WalletsReport: 'wallet.report',
  RevenueMarginsReport: 'reports.view',
  PurchasesReport: 'reports.view',
  InventoriesReport: 'reports.view',
  ProductsReport: 'reports.view',
  SupplierReturnsReport: 'reports.view',
  SupplierPaymentsReport: 'reports.view',
  StockMovementsReport: 'reports.view',
  StockTransfersReport: 'reports.view',
  CashRegistersReport: 'reports.view',
  DeliveriesReport: 'reports.view',
  AccountingJournalReport: 'reports.view',
  AccountingLedgerReport: 'reports.view',
  AccountingBalanceReport: 'reports.view',
  AccountingExceptionsReport: 'reports.view',
  Users: 'users.view',
  UserShow: 'users.view',
  AllSitesUsers: 'users.view',
  AllSitesUserShow: 'users.view',
  ActivityLogs: 'users.view',
  Sites: 'shops.view',
  AllSitesProductDatabase: 'products.view',
  SettingsGeneral: 'settings.view',
  SettingsPrinting: 'settings.view',
  SettingsInventory: 'settings.view',
  SettingsAccounting: 'settings.view',
  SettingsEcommerce: 'settings.view',
  ShopInfo: 'settings.view',
  DeliverySettings: 'settings.view',
  MovementSettings: 'settings.view',
  SiteShow: 'settings.view',
};

export function canBypassPermissions(user: User | null | undefined): boolean {
  const role = String(user?.role || '').toLowerCase();

  return BYPASS_ROLES.includes(role);
}

export function isSuperAdminUser(user: User | null | undefined): boolean {
  const role = String(user?.role || '').toLowerCase();

  return SUPERADMIN_ROLES.includes(role);
}

export function canAdministerUser(actor: User | null | undefined, target: Pick<User, 'role' | 'permissions'> | null | undefined): boolean {
  if (!actor || !target) return false;
  if (isSuperAdminUser(actor)) return true;
  const allowedRoles = isSupportUser(actor)
    ? ['gerant', 'caissier', 'utilisateur', 'demo']
    : ['caissier', 'utilisateur'];
  if (!allowedRoles.includes(target.role)) return false;
  if (canBypassPermissions(actor) || isSupportUser(actor)) return true;
  return (target.permissions || []).every((permission) => hasPermission(actor, permission));
}

export function isSupportUser(user: User | null | undefined): boolean {
  const role = String(user?.role || '').toLowerCase();

  return SUPPORT_ROLES.includes(role);
}

export function isDemoUser(user: User | null | undefined): boolean {
  const role = String(user?.role || '').toLowerCase();

  return DEMO_ROLES.includes(role);
}

export function hasPermission(user: User | null | undefined, permission?: string | null): boolean {
  if (!permission) return true;
  if (!user) return false;
  if (canBypassPermissions(user)) return true;
  if (isDemoUser(user) && DEMO_BLOCKED_PERMISSIONS.includes(permission)) return false;
  const permissions = Array.isArray(user.permissions) ? user.permissions : [];

  if (isDemoUser(user) && permissions.length === 0) return true;

  return permissions.includes(permission);
}

export function hasAnyPermission(user: User | null | undefined, permissions: string[]): boolean {
  return permissions.some((permission) => hasPermission(user, permission));
}

export function routePermission(route: Pick<RouteLocationNormalizedLoaded, 'name' | 'meta'>): string | null {
  const metaPermission = route.meta?.permission;

  if (typeof metaPermission === 'string' && metaPermission) {
    return metaPermission;
  }

  if (Array.isArray(route.meta?.permissionsAny) && route.meta.permissionsAny.length) return null;

  return ROUTE_PERMISSIONS[String(route.name || '')] || null;
}

export function routePermissions(route: Pick<RouteLocationNormalizedLoaded, 'name' | 'meta'>): string[] {
  const metaPermissions = Array.isArray(route.meta?.permissionsAll)
    ? route.meta.permissionsAll.filter((permission): permission is string => typeof permission === 'string' && Boolean(permission))
    : [];
  const primaryPermission = routePermission(route);

  return [...new Set([
    ...(primaryPermission ? [primaryPermission] : []),
    ...metaPermissions,
  ])];
}

export function hasAllPermissions(user: User | null | undefined, permissions: string[]): boolean {
  return permissions.every((permission) => hasPermission(user, permission));
}
